"use client"

import * as React from "react"
import { Label } from "@/components/ui/label"
import { Facebook, CheckCircle2, XCircle, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { messengerService, MessengerServiceError } from "@/services/messengerService"
import type { MessengerConnectionStatus } from "@/services/messengerService"
import { facebookOAuthService, FacebookOAuthError, MESSENGER_LOGIN_SCOPES } from "@/services/facebookOAuthService"
import { facebookService } from "@/services/facebookService"
import { notifyChannelConnectionChanged } from "@/lib/channel-connection-events"

interface MessengerConnectionProps {
  translations?: Record<string, unknown>
}

export function MessengerConnection({ translations }: MessengerConnectionProps) {
  const { t } = useTranslation()

  const [connectionStatus, setConnectionStatus] = React.useState<MessengerConnectionStatus | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [initialLoading, setInitialLoading] = React.useState(true)

  const getTranslation = React.useCallback((key: string): string => {
    if (translations) {
      const keys = key.split('.')
      let value: Record<string, unknown> = translations
      for (const k of keys) {
        value = value?.[k] as Record<string, unknown>
      }
      return typeof value === 'string' ? value : key
    }
    return t(key)
  }, [translations, t])

  React.useEffect(() => {
    const loadStatus = async () => {
      try {
        setInitialLoading(true)
        const status = await messengerService.getStatus()
        setConnectionStatus(status)
        setError(null)
      } catch (err) {
        console.error('Error loading Messenger status:', err)
        setError(err instanceof MessengerServiceError ? err.message : getTranslation('settings.messenger.error_loading'))
        setConnectionStatus({ connected: false })
      } finally {
        setInitialLoading(false)
      }
    }

    loadStatus()
  }, [getTranslation])

  const handleConnect = async () => {
    try {
      setLoading(true)
      setError(null)

      // Use standard token flow (no config_id needed for Messenger)
      // Config_id requires code flow which isn't compatible with immediate token usage
      const accessToken = await facebookOAuthService.login(undefined, 'token', MESSENGER_LOGIN_SCOPES)

      if (!accessToken) {
        setError(getTranslation('settings.messenger.error_no_token') || 'Access token is required')
        setLoading(false)
        return
      }

      // Fetch pages and use the first one - page selection is handled by Meta integration
      try {
        const pages = await facebookService.getFacebookPages(accessToken)

        if (pages.length === 0) {
          setError(getTranslation('settings.messenger.error_no_pages') || 'No Facebook pages found. Please create a page first.')
          return
        }

        // Use the first page automatically
        const firstPage = pages[0]
        const status = await messengerService.connect({
          page_id: firstPage.id,
          page_access_token: firstPage.access_token,
          user_access_token: accessToken,
        })

        setConnectionStatus(status)
        notifyChannelConnectionChanged({ channel: 'messenger', connected: true })
      } catch (pageError) {
        console.error('Error fetching pages:', pageError)
        setError(getTranslation('settings.messenger.error_fetching_pages') || 'Error fetching Facebook pages')
      }
    } catch (err) {
      console.error('Error connecting to Messenger:', err)
      if (err instanceof FacebookOAuthError) {
        if (err.message.includes('cancelled') || err.message.includes('did not authorize')) {
          setError(null)
        } else {
          setError(err.message)
        }
      } else {
        setError(
          err instanceof MessengerServiceError
            ? err.message
            : getTranslation('settings.messenger.error_connecting') || 'Failed to connect to Messenger'
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      setLoading(true)
      setError(null)

      await messengerService.disconnect()
      setConnectionStatus({ connected: false })
      notifyChannelConnectionChanged({ channel: 'messenger', connected: false })
    } catch (err) {
      console.error('Error disconnecting from Messenger:', err)
      setError(err instanceof MessengerServiceError ? err.message : getTranslation('settings.messenger.error_disconnecting'))
    } finally {
      setLoading(false)
    }
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">{getTranslation('settings.messenger.loading')}</span>
      </div>
    )
  }

  const isConnected = connectionStatus?.connected === true

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2 flex-shrink-0 relative z-10">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isConnected
            ? 'bg-success/10 text-success'
            : 'bg-muted text-muted-foreground'
            }`}>
            {isConnected ? (
              <>
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">{getTranslation('settings.messenger.connected_status')}</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">{getTranslation('settings.messenger.disconnected_status')}</span>
              </>
            )}
          </div>
        </div>

        {isConnected ? (
          <Button
            variant="outline"
            size="default"
            onClick={handleDisconnect}
            disabled={loading}
            className="gap-2 shrink-0 w-full sm:w-auto relative z-0"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{getTranslation('settings.messenger.loading')}</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4" />
                <span>{getTranslation('settings.messenger.disconnect_button')}</span>
              </>
            )}
          </Button>
        ) : (
          <Button
            variant="default"
            size="default"
            onClick={handleConnect}
            disabled={loading}
            className="gap-2 shrink-0 w-full sm:w-auto relative z-0"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{getTranslation('settings.messenger.loading')}</span>
              </>
            ) : (
              <>
                <Facebook className="h-4 w-4" />
                <span>{getTranslation('settings.messenger.connect_button')}</span>
              </>
            )}
          </Button>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {isConnected && connectionStatus && (
        <>
          <Separator />
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{getTranslation('settings.messenger.connection_info')}</h3>

            {connectionStatus.page_id && (
              <div>
                <Label className="text-xs text-muted-foreground">
                  {getTranslation('settings.messenger.page_id')}
                </Label>
                <p className="text-sm font-mono text-muted-foreground mt-1">{connectionStatus.page_id}</p>
              </div>
            )}

            {connectionStatus.metadata?.page_name && (
              <div>
                <Label className="text-xs text-muted-foreground">
                  {getTranslation('settings.messenger.page_name')}
                </Label>
                <p className="text-sm mt-1">{connectionStatus.metadata.page_name}</p>
              </div>
            )}

            {connectionStatus.metadata?.page_category && (
              <div>
                <Label className="text-xs text-muted-foreground">
                  {getTranslation('settings.messenger.page_category')}
                </Label>
                <p className="text-sm mt-1">{connectionStatus.metadata.page_category}</p>
              </div>
            )}

            {(connectionStatus.metadata?.webhook_subscription_fields?.length ?? 0) > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground">
                  {getTranslation('settings.messenger.webhook_events')}
                </Label>
                <ul className="mt-1 space-y-1">
                  {connectionStatus.metadata?.webhook_subscription_fields?.map((field) => (
                    <li key={field} className="flex items-center gap-2 text-sm font-mono">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      {field}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </>
      )}

      {!isConnected && !loading && (
        <div className="p-4 rounded-md bg-muted/50 border border-dashed">
          <p className="text-sm text-muted-foreground text-center">
            {getTranslation('settings.messenger.description')}
          </p>
        </div>
      )}
    </div>
  )
}

